/**
 * TaxReporting - Tax Lot Tracking & Reporting
 *
 * Computes realized gains/losses from closed orders, identifies wash sales,
 * estimates taxes based on configurable rates, and exports to CSV.
 *
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Receipt,
  RefreshCw,
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  DollarSign,
  Clock,
  FileSpreadsheet,
  Info,
  ChevronDown,
  ChevronUp,
  Filter,
  Loader2,
  XCircle,
  Landmark,
  Scale,
} from 'lucide-react';
import { brokerAPI, type BrokerOrder } from '../../api/broker';

// ─── Types ──────────────────────────────────────────────────────────────────

interface TaxLot {
  id: string;
  symbol: string;
  qty: number;
  dateAcquired: Date;
  dateSold: Date;
  costBasis: number;
  proceeds: number;
  gainLoss: number;
  gainLossPct: number;
  term: 'Short-Term' | 'Long-Term';
  washSale: boolean;
  buyOrderId: string;
  sellOrderId: string;
}

interface TaxSummary {
  totalRealizedGains: number;
  shortTermGains: number;
  longTermGains: number;
  totalLosses: number;
  washSaleDisallowed: number;
  estimatedTax: number;
  tradeCount: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const AVAILABLE_YEARS = [2024, 2025, 2026] as const;

const LONG_TERM_DAYS = 365;
const WASH_SALE_WINDOW_DAYS = 30;

// ─── Component ──────────────────────────────────────────────────────────────

export default function TaxReporting() {
  const [orders, setOrders] = useState<BrokerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [federalRate, setFederalRate] = useState(22);
  const [stateRate, setStateRate] = useState(5);
  const [sortField, setSortField] = useState<'dateSold' | 'symbol' | 'gainLoss'>('dateSold');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showRatesPanel, setShowRatesPanel] = useState(false);

  // ─── Data Fetching ────────────────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await brokerAPI.getOrders('closed', 500);
      setOrders(data.orders || []);
    } catch (err: any) {
      console.error('Failed to fetch orders:', err);
      setError(err.message || 'Failed to load closed orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ─── Build Tax Lots ───────────────────────────────────────────────────

  const taxLots = useMemo<TaxLot[]>(() => {
    if (orders.length === 0) return [];

    // Separate buy and sell fills
    const buys: BrokerOrder[] = [];
    const sells: BrokerOrder[] = [];

    for (const o of orders) {
      if (o.status !== 'filled' && o.status !== 'partially_filled') continue;
      if (!o.filled_at || !o.filled_avg_price || !o.filled_qty) continue;
      if (o.side === 'buy') buys.push(o);
      else if (o.side === 'sell') sells.push(o);
    }

    // Sort buys chronologically for FIFO matching
    buys.sort(
      (a, b) => new Date(a.filled_at!).getTime() - new Date(b.filled_at!).getTime()
    );

    // Track remaining qty per buy order
    const buyRemaining = new Map<string, number>();
    buys.forEach((b) => buyRemaining.set(b.id, parseFloat(b.filled_qty!)));

    const lots: TaxLot[] = [];
    let lotIndex = 0;

    for (const sell of sells) {
      const sellDate = new Date(sell.filled_at!);
      const sellPrice = parseFloat(sell.filled_avg_price!);
      let sellQtyLeft = parseFloat(sell.filled_qty!);

      // FIFO matching against buys of the same symbol
      for (const buy of buys) {
        if (sellQtyLeft <= 0) break;
        if (buy.symbol !== sell.symbol) continue;

        const remainingBuyQty = buyRemaining.get(buy.id) || 0;
        if (remainingBuyQty <= 0) continue;

        const matchQty = Math.min(sellQtyLeft, remainingBuyQty);
        const buyDate = new Date(buy.filled_at!);
        const buyPrice = parseFloat(buy.filled_avg_price!);
        const costBasis = buyPrice * matchQty;
        const proceeds = sellPrice * matchQty;
        const gainLoss = proceeds - costBasis;
        const holdDays = Math.floor(
          (sellDate.getTime() - buyDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        lots.push({
          id: `lot-${lotIndex++}`,
          symbol: sell.symbol,
          qty: matchQty,
          dateAcquired: buyDate,
          dateSold: sellDate,
          costBasis,
          proceeds,
          gainLoss,
          gainLossPct: costBasis > 0 ? (gainLoss / costBasis) * 100 : 0,
          term: holdDays >= LONG_TERM_DAYS ? 'Long-Term' : 'Short-Term',
          washSale: false, // computed below
          buyOrderId: buy.id,
          sellOrderId: sell.id,
        });

        buyRemaining.set(buy.id, remainingBuyQty - matchQty);
        sellQtyLeft -= matchQty;
      }
    }

    // ── Wash Sale Detection ───────────────────────────────────────────
    // A loss is a wash sale if the same symbol was bought within 30 days
    // before or after the sale at a loss.
    const lossingLots = lots.filter((l) => l.gainLoss < 0);

    for (const lot of lossingLots) {
      const sellTime = lot.dateSold.getTime();
      const windowMs = WASH_SALE_WINDOW_DAYS * 24 * 60 * 60 * 1000;

      const hasRepurchase = buys.some((b) => {
        if (b.symbol !== lot.symbol) return false;
        const buyTime = new Date(b.filled_at!).getTime();
        return (
          buyTime >= sellTime - windowMs &&
          buyTime <= sellTime + windowMs &&
          b.id !== lot.buyOrderId
        );
      });

      if (hasRepurchase) {
        lot.washSale = true;
      }
    }

    return lots;
  }, [orders]);

  // ─── Filtered & Sorted Lots ───────────────────────────────────────────

  const filteredLots = useMemo(() => {
    const yearLots = taxLots.filter(
      (lot) => lot.dateSold.getFullYear() === selectedYear
    );

    return yearLots.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'dateSold') {
        cmp = a.dateSold.getTime() - b.dateSold.getTime();
      } else if (sortField === 'symbol') {
        cmp = a.symbol.localeCompare(b.symbol);
      } else if (sortField === 'gainLoss') {
        cmp = a.gainLoss - b.gainLoss;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [taxLots, selectedYear, sortField, sortDir]);

  // ─── Tax Summary ──────────────────────────────────────────────────────

  const summary = useMemo<TaxSummary>(() => {
    let shortTermGains = 0;
    let longTermGains = 0;
    let totalLosses = 0;
    let washSaleDisallowed = 0;

    for (const lot of filteredLots) {
      if (lot.gainLoss >= 0) {
        if (lot.term === 'Short-Term') shortTermGains += lot.gainLoss;
        else longTermGains += lot.gainLoss;
      } else {
        if (lot.washSale) {
          washSaleDisallowed += Math.abs(lot.gainLoss);
        } else {
          totalLosses += Math.abs(lot.gainLoss);
        }
      }
    }

    const totalRealizedGains = shortTermGains + longTermGains - totalLosses;
    const combinedRate = (federalRate + stateRate) / 100;
    const estimatedTax = Math.max(0, totalRealizedGains * combinedRate);

    return {
      totalRealizedGains,
      shortTermGains,
      longTermGains,
      totalLosses,
      washSaleDisallowed,
      estimatedTax,
      tradeCount: filteredLots.length,
    };
  }, [filteredLots, federalRate, stateRate]);

  // ─── CSV Export ───────────────────────────────────────────────────────

  const handleExportCSV = () => {
    if (filteredLots.length === 0) return;

    const headers = [
      'Date Acquired',
      'Date Sold',
      'Symbol',
      'Qty',
      'Cost Basis',
      'Proceeds',
      'Gain/Loss',
      'Gain/Loss %',
      'Term',
      'Wash Sale',
    ];

    const rows = filteredLots.map((lot) => [
      lot.dateAcquired.toLocaleDateString(),
      lot.dateSold.toLocaleDateString(),
      lot.symbol,
      lot.qty.toString(),
      lot.costBasis.toFixed(2),
      lot.proceeds.toFixed(2),
      lot.gainLoss.toFixed(2),
      lot.gainLossPct.toFixed(2),
      lot.term,
      lot.washSale ? 'Yes' : 'No',
    ]);

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax-report-${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Sort Toggle ──────────────────────────────────────────────────────

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'desc' ? (
      <ChevronDown className="w-3 h-3 inline ml-1" />
    ) : (
      <ChevronUp className="w-3 h-3 inline ml-1" />
    );
  };

  // ─── Helpers ──────────────────────────────────────────────────────────

  const fmtCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(n);

  const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-6">
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Receipt className="w-8 h-8 text-amber-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Tax Reporting</h1>
            <p className="text-gray-400 text-sm">
              Tax lot tracking, wash sale detection, and estimated tax calculations
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Year Selector */}
          <div className="flex items-center gap-2 bg-[#12121a] border border-[#1e1e2d] rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="bg-transparent text-white text-sm focus:outline-none cursor-pointer"
            >
              {AVAILABLE_YEARS.map((y) => (
                <option key={y} value={y} className="bg-[#12121a]">
                  {y}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setShowRatesPanel(!showRatesPanel)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showRatesPanel
                ? 'bg-amber-600 text-white'
                : 'bg-[#12121a] border border-[#1e1e2d] text-gray-300 hover:border-amber-500/50'
            }`}
          >
            <Scale className="w-4 h-4" />
            Tax Rates
          </button>

          <button
            onClick={fetchOrders}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-[#12121a] border border-[#1e1e2d] rounded-lg hover:border-amber-500/50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <button
            onClick={handleExportCSV}
            disabled={filteredLots.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* ── Error ─────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Tax Rate Inputs ───────────────────────────────────────── */}
      {showRatesPanel && (
        <div className="bg-[#12121a] border border-amber-500/30 rounded-xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Landmark className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Tax Rate Configuration</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Federal Tax Rate: <span className="text-amber-400 font-medium">{federalRate}%</span>
              </label>
              <input
                type="range"
                min={0}
                max={50}
                step={1}
                value={federalRate}
                onChange={(e) => setFederalRate(Number(e.target.value))}
                className="w-full accent-amber-500"
              />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                State Tax Rate: <span className="text-amber-400 font-medium">{stateRate}%</span>
              </label>
              <input
                type="range"
                min={0}
                max={15}
                step={0.5}
                value={stateRate}
                onChange={(e) => setStateRate(Number(e.target.value))}
                className="w-full accent-amber-500"
              />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>0%</span>
                <span>7.5%</span>
                <span>15%</span>
              </div>
            </div>
            <div className="flex items-center">
              <div className="bg-[#0a0a0f] rounded-lg p-4 w-full">
                <div className="text-sm text-gray-400 mb-1">Combined Rate</div>
                <div className="text-2xl font-bold text-amber-400">{federalRate + stateRate}%</div>
                <div className="text-xs text-gray-500 mt-1">
                  Federal {federalRate}% + State {stateRate}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Summary Cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard
          icon={<DollarSign className="w-5 h-5" />}
          label="Total Realized Gains"
          value={fmtCurrency(summary.totalRealizedGains)}
          color={summary.totalRealizedGains >= 0 ? 'emerald' : 'red'}
        />
        <SummaryCard
          icon={<Clock className="w-5 h-5" />}
          label="Short-Term Gains"
          value={fmtCurrency(summary.shortTermGains)}
          subtext={`${summary.tradeCount} trades`}
          color="blue"
        />
        <SummaryCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Long-Term Gains"
          value={fmtCurrency(summary.longTermGains)}
          color="violet"
        />
        <SummaryCard
          icon={<Landmark className="w-5 h-5" />}
          label="Estimated Tax"
          value={fmtCurrency(summary.estimatedTax)}
          subtext={`@ ${federalRate + stateRate}% combined`}
          color="amber"
        />
      </div>

      {/* ── Secondary Stats ───────────────────────────────────────── */}
      {(summary.totalLosses > 0 || summary.washSaleDisallowed > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#12121a] rounded-xl p-4 border border-[#1e1e2d]">
            <div className="flex items-center gap-2 text-red-400 text-sm mb-1">
              <TrendingDown className="w-4 h-4" />
              Total Losses
            </div>
            <div className="text-xl font-bold text-red-400">
              -{fmtCurrency(summary.totalLosses)}
            </div>
          </div>
          {summary.washSaleDisallowed > 0 && (
            <div className="bg-[#12121a] rounded-xl p-4 border border-yellow-500/30">
              <div className="flex items-center gap-2 text-yellow-400 text-sm mb-1">
                <AlertTriangle className="w-4 h-4" />
                Wash Sale Disallowed
              </div>
              <div className="text-xl font-bold text-yellow-400">
                {fmtCurrency(summary.washSaleDisallowed)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Losses deferred under wash sale rules
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Trade Log Table ───────────────────────────────────────── */}
      <div className="bg-[#12121a] border border-[#1e1e2d] rounded-xl overflow-hidden mb-6">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1e1e2d]">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <FileSpreadsheet className="w-4 h-4" />
            <span>
              {filteredLots.length} tax lot{filteredLots.length !== 1 ? 's' : ''} for {selectedYear}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Filter className="w-3 h-3" />
            Click column headers to sort
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
          </div>
        ) : filteredLots.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No Tax Lots for {selectedYear}
            </h3>
            <p className="text-gray-500 text-sm">
              Closed and filled trades will appear here after matching buy/sell pairs.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1e1e2d]">
                  <th
                    className="text-left text-gray-400 font-medium px-4 py-3 cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => toggleSort('dateSold')}
                  >
                    Date Acquired
                  </th>
                  <th
                    className="text-left text-gray-400 font-medium px-4 py-3 cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => toggleSort('dateSold')}
                  >
                    Date Sold <SortIcon field="dateSold" />
                  </th>
                  <th
                    className="text-left text-gray-400 font-medium px-4 py-3 cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => toggleSort('symbol')}
                  >
                    Symbol <SortIcon field="symbol" />
                  </th>
                  <th className="text-right text-gray-400 font-medium px-4 py-3">Qty</th>
                  <th className="text-right text-gray-400 font-medium px-4 py-3">Cost Basis</th>
                  <th className="text-right text-gray-400 font-medium px-4 py-3">Proceeds</th>
                  <th
                    className="text-right text-gray-400 font-medium px-4 py-3 cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => toggleSort('gainLoss')}
                  >
                    Gain/Loss <SortIcon field="gainLoss" />
                  </th>
                  <th className="text-center text-gray-400 font-medium px-4 py-3">Term</th>
                  <th className="text-center text-gray-400 font-medium px-4 py-3">Wash Sale</th>
                </tr>
              </thead>
              <tbody>
                {filteredLots.map((lot) => (
                  <tr
                    key={lot.id}
                    className={`border-b border-[#1e1e2d]/50 hover:bg-[#1a1a25] transition-colors ${
                      lot.washSale ? 'bg-yellow-500/5' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                      {lot.dateAcquired.toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {lot.dateSold.toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-white font-medium">{lot.symbol}</td>
                    <td className="px-4 py-3 text-white text-right">
                      {lot.qty % 1 === 0 ? lot.qty : lot.qty.toFixed(4)}
                    </td>
                    <td className="px-4 py-3 text-gray-300 text-right">
                      {fmtCurrency(lot.costBasis)}
                    </td>
                    <td className="px-4 py-3 text-gray-300 text-right">
                      {fmtCurrency(lot.proceeds)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div
                        className={`font-medium ${
                          lot.gainLoss >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {fmtCurrency(lot.gainLoss)}
                      </div>
                      <div
                        className={`text-xs ${
                          lot.gainLoss >= 0 ? 'text-emerald-500/70' : 'text-red-500/70'
                        }`}
                      >
                        {fmtPct(lot.gainLossPct)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-xs rounded ${
                          lot.term === 'Long-Term'
                            ? 'bg-violet-500/20 text-violet-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}
                      >
                        {lot.term === 'Long-Term' ? (
                          <TrendingUp className="w-3 h-3 mr-1" />
                        ) : (
                          <Clock className="w-3 h-3 mr-1" />
                        )}
                        {lot.term}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {lot.washSale ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                          <AlertTriangle className="w-3 h-3" />
                          Wash
                        </span>
                      ) : (
                        <span className="text-gray-600">--</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Disclaimer ────────────────────────────────────────────── */}
      <div className="bg-[#12121a] border border-[#1e1e2d] rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-500 leading-relaxed">
          <p className="font-medium text-gray-400 mb-1">Disclaimer</p>
          <p>
            This tax reporting tool provides estimates for informational purposes only and does not
            constitute tax advice. Calculations may not account for all tax situations including but
            not limited to: options trades, short sales, corporate actions, foreign tax credits, or
            state-specific rules. Wash sale detection is based on a simplified 30-day window and may
            not cover all IRS wash sale scenarios. <strong className="text-gray-400">Please consult
            a qualified tax professional</strong> before making any tax-related decisions. You are
            solely responsible for the accuracy of your tax filings.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SummaryCard({
  icon,
  label,
  value,
  subtext,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext?: string;
  color: 'emerald' | 'red' | 'blue' | 'violet' | 'amber';
}) {
  const colorMap = {
    emerald: 'text-emerald-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    violet: 'text-violet-400',
    amber: 'text-amber-400',
  };
  const iconColor = colorMap[color];

  return (
    <div className="bg-[#12121a] rounded-xl p-4 border border-[#1e1e2d]">
      <div className={`flex items-center gap-2 ${iconColor} text-sm mb-1`}>
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-bold ${iconColor}`}>{value}</div>
      {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
    </div>
  );
}
